import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BrandLogo } from "@/components/BrandLogo";
import { Shield } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export const LandingFooter = forwardRef<HTMLElement>(function LandingFooter(_props, ref) {
  const { t } = useTranslation();

  const footerLinks = {
    [t("footer.product")]: [
      { label: "Packet Scanner", href: "/product/packet-scanner" },
      { label: t("footer.documentation"), href: "/docs" },
      // The methodology was published 2026-08-18 but reachable only from a
      // result card. Measured over the 30 days before that: 41 people on the
      // landing page, 1 person on the methodology. A trust product that will
      // not say how it scores, where prospects actually look, is not selling
      // trust. Deliberately in Product, not Legal: it is a capability, not a
      // disclaimer.
      { label: "How scoring works", href: "/trust-score" },
      { label: t("nav.pricing"), href: "/pricing" },
      { label: "Blog", href: "/blog" },
      { label: t("footer.status"), href: "/status" },
    ],
    [t("footer.verticals")]: [
      { label: t("footer.freightLogistics"), href: "/carriers" },
    ],
    [t("footer.company")]: [
      { label: t("nav.about"), href: "/about" },
      { label: t("footer.support"), href: "mailto:support@cipherandrow.com" },
      { label: t("auth.signUp"), href: "/signup" },
      { label: t("nav.signIn"), href: "/login" },
    ],
    [t("footer.legal")]: [
      { label: t("footer.termsOfService"), href: "/terms" },
      { label: t("footer.privacyPolicy"), href: "/privacy" },
      { label: "Cookie Policy", href: "/cookies" },
      { label: t("footer.apiTerms"), href: "/api-terms" },
      { label: "Security", href: "/security" },
    ],
    "Follow Us": [
      { label: "LinkedIn", href: "https://www.linkedin.com/company/cipher-and-row" },
      { label: "X (Twitter)", href: "https://x.com/cipherandrow" },
      { label: "Facebook", href: "https://www.facebook.com/people/Cipher-Row/61584034991875/" },
      { label: "Instagram", href: "https://www.instagram.com/cipherandrow/" },
    ],
  };

  return (
    <footer ref={ref} data-cr-footer className="gradient-mesh relative">
      {/* Top glow divider instead of plain border */}
      <div className="gradient-divider-glow" />

      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8 py-16">
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <div className="mb-5">
              <BrandLogo to="/" height="h-10" className="font-display" />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed mb-4 max-w-[200px]">
              {t("footer.tagline")}
            </p>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="flex items-center gap-1.5 rounded border border-primary/20 bg-primary/5 px-2.5 py-1">
                <Shield className="h-3 w-3 text-primary" />
                <span className="text-xs font-semibold text-primary">{t("footer.encrypted")}</span>
              </div>
              <div className="flex items-center gap-1.5 rounded border border-border bg-card px-2.5 py-1">
                <span className="text-xs font-semibold text-muted-foreground">{t("footer.auditTrail")}</span>
              </div>
            </div>
            <LanguageSwitcher variant="full" />
          </div>

          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">{category}</p>
              <ul className="space-y-1">
                {links.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith("http") || link.href.startsWith("mailto:") ? (
                      <a href={link.href} target={link.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200 inline-flex items-center min-h-[44px] py-1">
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.href} className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200 inline-flex items-center min-h-[44px] py-1">
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="gradient-divider py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span>© {new Date().getFullYear()} Cipher & Row</span>
            <span className="text-border">·</span>
            <span>{t("footer.allRightsReserved")}</span>
            <span className="text-border">·</span>
            <span className="italic">Made for freight, by freight.</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            {/* Neutral link only — no hardcoded "all systems operational"
                claim or fake live pulse. The /status page is the source of
                truth for uptime. */}
            <Link
              to="/status"
              className="flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 hover:border-primary/40 hover:text-primary transition-colors"
            >
              <span className="font-medium">System status →</span>
            </Link>
            <span className="hidden sm:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider">
              <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
              build {(import.meta.env.VITE_BUILD_SHA as string | undefined)?.slice(0, 7) ?? "live"} · {new Date().toISOString().slice(0, 10)}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
});
